import { api } from "@/lib/api";
import type { TicketListItem } from "@/features/tickets/api";

export type WorkType = "onsite" | "remote" | "travel" | "diagnosis" | "other";

export interface TimeLog {
  id: string;
  ticket_id: string;
  engineer_id: string;
  engineer_name: string;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  work_type: WorkType;
  notes: string | null;
  is_billable: boolean;
  checkin_distance_m: number | null;
  checkin_verified: boolean;
  is_open: boolean;
}

export interface TodaysJobs {
  jobs: TicketListItem[];
  open_time_log: TimeLog | null;
  minutes_logged_30d: number;
  visits_30d: number;
}

export interface ServiceReport {
  id: string;
  report_number: string;
  ticket_id: string;
  ticket_number: string;
  engineer_id: string;
  engineer_name: string;
  work_summary: string;
  parts_used: { name?: string; quantity?: number; serial?: string }[] | null;
  recommendations: string | null;
  total_minutes: number;
  signed_by_name: string | null;
  signed_at: string | null;
  is_signed: boolean;
  created_at: string;
}

export function todaysJobs(): Promise<TodaysJobs> {
  return api.get<TodaysJobs>("/engineer/today");
}

export function checkIn(
  ticketId: string,
  body: {
    latitude?: number | null;
    longitude?: number | null;
    work_type?: WorkType;
    notes?: string | null;
  },
): Promise<TimeLog> {
  return api.post<TimeLog>(`/tickets/${ticketId}/check-in`, body);
}

export function checkOut(body: {
  latitude?: number | null;
  longitude?: number | null;
  notes?: string | null;
  is_billable?: boolean;
}): Promise<TimeLog> {
  return api.post<TimeLog>("/engineer/check-out", body);
}

export function ticketTimeLogs(ticketId: string): Promise<TimeLog[]> {
  return api.get<TimeLog[]>(`/tickets/${ticketId}/time-logs`);
}

export function getServiceReport(ticketId: string): Promise<ServiceReport | null> {
  return api.get<ServiceReport | null>(`/tickets/${ticketId}/service-report`);
}

export function createServiceReport(
  ticketId: string,
  body: {
    work_summary: string;
    parts_used?: { name: string; quantity: number; serial?: string | null }[];
    recommendations?: string | null;
  },
): Promise<ServiceReport> {
  return api.post<ServiceReport>(`/tickets/${ticketId}/service-report`, body);
}

/** Path relative to the API base — pass to downloadFile, which prepends /api/v1. */
export function serviceReportPdfUrl(reportId: string): string {
  return `/service-reports/${reportId}/pdf`;
}

/**
 * Best-effort browser position.
 *
 * Resolves to null rather than rejecting: a denied or unavailable fix must
 * not stop an engineer checking in, and the server records the absence.
 */
export function currentPosition(
  timeoutMs = 8000,
): Promise<{ latitude: number; longitude: number } | null> {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60_000 },
    );
  });
}

/** "2h 35m" from seconds; open logs count up from their start. */
export function formatDuration(seconds: number | null): string {
  if (seconds === null) return "—";
  const total = Math.max(0, Math.floor(seconds / 60));
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export function elapsedSeconds(startedAt: string, now: Date = new Date()): number {
  return Math.max(0, Math.floor((now.getTime() - new Date(startedAt).getTime()) / 1000));
}
